import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Copy, ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AssetsApi, type Asset } from '@/services/assets/assetsApi'
import { PreviewApi, type PreviewLayoutParameter } from '@/services/creatives/previewApi'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import { OrdersApi } from '@/services/orders/ordersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import type { Creative } from '@/services/creatives/creativesApi'

/** Falls back to a square when the creative carries no dimensions of its own. */
export function previewSize(creative: Creative) {
  return {
    width: creative.width || creative.height || 300,
    height: creative.height || creative.width || 300,
  }
}

/** The creative's layout parameters, its asset components resolved into what the renderer needs. */
export function previewLayoutParameters(
  creative: Creative,
  assets: Asset[]
): Record<string, PreviewLayoutParameter> {
  const params: Record<string, PreviewLayoutParameter> = {}
  for (const [tag, value] of Object.entries(creative.layoutParameters ?? {})) {
    if (value && typeof value === 'object' && 'id' in value) {
      const asset = assets.find((a) => a.id === value.id)
      params[tag] = asset
        ? {
            cdnId: asset.cdnId,
            previewCdnId: asset.previewCdnId,
            width: asset.width,
            height: asset.height,
          }
        : null
    } else {
      params[tag] = value
    }
  }
  return params
}

/** The assets a creative's preview resolves its components from. */
export function useCreativePreviewAssets(creative: Creative | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['creative-assets-preview', creative?.id],
    queryFn: () => AssetsApi.getByCreative(creative!.id),
    enabled: enabled && !!creative,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Builds a link that opens a real page with this creative showing in its ad
 * units, for the page URL the user gives.
 */
export function CreativeLivePreviewForm({
  creative,
  enabled,
}: {
  creative: Creative
  /** False while the surrounding view is closed, so nothing loads before it is needed. */
  enabled: boolean
}) {
  const { t } = useTranslation()
  const [viewUrl, setViewUrl] = useState('')
  const [liveLink, setLiveLink] = useState('')
  const { data: assets = [] } = useCreativePreviewAssets(creative, enabled)

  // The live preview link is issued per team, which the creative reaches via its order
  const { data: lineItem } = useQuery({
    queryKey: ['lineitem', creative.lineItemId],
    queryFn: () => LineItemsApi.getById(creative.lineItemId),
    enabled: enabled && !!creative.lineItemId,
    staleTime: 5 * 60 * 1000,
  })
  const { data: order } = useQuery({
    queryKey: ['order', lineItem?.orderId],
    queryFn: () => OrdersApi.getById(lineItem!.orderId),
    enabled: enabled && !!lineItem?.orderId,
    staleTime: 5 * 60 * 1000,
  })

  const liveMutation = useMutation({
    mutationFn: async () => {
      const { width, height } = previewSize(creative)
      return PreviewApi.createLiveLink({
        viewUrl,
        teamId: order?.teamId ?? '',
        layoutId: creative.layoutId,
        width,
        height,
        layoutParameters: previewLayoutParameters(creative, assets),
      })
    },
    onSuccess: (link) => setLiveLink(link),
    onError: (e) =>
      toast.error(t('creatives.livePreviewFailed'), { description: apiErrorDescription(e) }),
  })

  // The page link carries the line item and creative so the ad units show this creative
  const pageLink = viewUrl
    ? `${viewUrl}${viewUrl.includes('?') ? '&' : '?'}adn-lp-li=${creative.lineItemId ?? ''}&adn-lp-c=${creative.id}`
    : ''

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-(--ad-text-muted)">{t('creatives.livePreviewIntro')}</p>
      <Alert>
        <AlertDescription>
          <ul className="list-disc pl-4 space-y-1 text-xs">
            <li>{t('creatives.livePreviewNoteTeam')}</li>
            <li>{t('creatives.livePreviewNoteSize')}</li>
            <li>{t('creatives.livePreviewNoteDeliverable')}</li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-2">
        <Label htmlFor="live-preview-url">{t('creatives.livePreviewUrl')}</Label>
        <div className="flex gap-2">
          <Input
            id="live-preview-url"
            type="url"
            value={viewUrl}
            onChange={(e) => setViewUrl(e.target.value)}
            placeholder="https://example.com/article"
          />
          <Button
            type="button"
            onClick={() => liveMutation.mutate()}
            disabled={!viewUrl || !order?.teamId || liveMutation.isPending}
          >
            {liveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {t('common.create')}
          </Button>
        </div>
      </div>

      {liveLink && (
        <div className="flex flex-col gap-2">
          <Label>{t('creatives.livePreviewLink')}</Label>
          <div className="flex gap-2">
            <Input readOnly value={pageLink} className="font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(pageLink)
                toast.success(t('common.copied'))
              }}
            >
              <Copy className="size-4" />
            </Button>
            <Button type="button" variant="outline" asChild>
              <a href={pageLink} target="_blank" rel="noreferrer">
                <ExternalLink className="size-4" />
              </a>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
